import {
  Calculator,
  WarningCircle,
  CheckCircle,
  ShoppingBag,
} from "@phosphor-icons/react";

export function BudgetCalculator({ lang, c, budget, setBudget, plan, ownedIds, onToggleOwn, isAuthenticated, totalItems }) {
  const tierLabel = c.budget.tiers[plan.tier];
  const tip = c.budget[`tip_${plan.tier}`];
  const ownedCount = plan.owned.length;

  const renderItem = (item, index, muted = false) => {
    const isOwned = ownedIds.has(item.id);
    return (
      <li
        key={item.id}
        className={`flex items-center gap-2 text-xs group ${muted ? "opacity-70" : ""}`}
        data-testid={`gear-item-${item.id}`}
      >
        <input
          type="checkbox"
          checked={isOwned}
          onChange={() => onToggleOwn(item.id)}
          className="w-3.5 h-3.5 rounded border-white/20 bg-[#121214] accent-[#F5A524] cursor-pointer shrink-0"
          data-testid={`gear-checkbox-${item.id}`}
          aria-label={isOwned ? c.budget.checked_label : c.budget.uncheck_label}
        />
        <span className={`flex-1 truncate ${isOwned ? "line-through text-[#52525B]" : "text-[#D4D4D8]"}`}>
          <span className="text-[#71717A] mr-1">{index + 1}.</span>
          {item.name}
        </span>
        <span className={`font-['JetBrains_Mono'] shrink-0 ${isOwned ? "text-[#52525B]" : "text-[#F5A524]"}`}>
          ${item.cost}
        </span>
      </li>
    );
  };

  return (
    <div
      className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6"
      data-testid="budget-calculator"
    >
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Calculator weight="fill" className="w-5 h-5 text-[#F5A524]" />
          <h2 className="font-['Outfit'] text-white text-xl font-medium">
            {c.budget.title}
          </h2>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#71717A]">{c.budget.owned_label}:</span>
          <span className="text-emerald-400 font-['JetBrains_Mono']" data-testid="owned-count">
            {ownedCount} / {totalItems}
          </span>
          {plan.ownedValue > 0 && (
            <span className="text-[#71717A] font-['JetBrains_Mono']">
              (${plan.ownedValue.toLocaleString()} {c.budget.saved})
            </span>
          )}
        </div>
      </div>
      <p className="text-[#A1A1AA] text-sm leading-relaxed mb-5">{c.budget.intro}</p>

      {!isAuthenticated && (
        <div className="bg-[#F5A524]/5 border border-[#F5A524]/20 rounded-lg p-3 mb-4 text-xs text-[#D4D4D8]">
          {c.budget.signin_hint}
        </div>
      )}

      {/* Budget input */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 mb-5">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <label className="text-[#71717A] text-xs uppercase tracking-[0.15em] font-semibold">
            {c.budget.label}
          </label>
          <div className="flex items-baseline gap-2">
            <span className="font-['JetBrains_Mono'] text-3xl font-bold text-[#F5A524]" data-testid="budget-value">
              ${budget.toLocaleString()}
            </span>
            <span className="text-[#71717A] text-xs uppercase tracking-widest">{tierLabel}</span>
          </div>
        </div>
        <input
          type="range"
          min="200"
          max="12000"
          step="50"
          value={budget}
          onChange={(e) => setBudget(parseInt(e.target.value, 10))}
          className="w-full accent-[#F5A524] cursor-pointer"
          data-testid="budget-slider"
        />
        <div className="flex flex-wrap gap-2 mt-3">
          {[500, 1500, 4000, 8000, 12000].map((preset) => (
            <button
              key={preset}
              onClick={() => setBudget(preset)}
              className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                budget === preset
                  ? "bg-[#F5A524] text-[#0A0A0A] border-[#F5A524]"
                  : "bg-transparent text-[#A1A1AA] border-white/10 hover:border-white/30 hover:text-white"
              }`}
              data-testid={`budget-preset-${preset}`}
            >
              ${preset.toLocaleString()}
            </button>
          ))}
        </div>
      </div>

      {/* Tier tip */}
      <div className="bg-[#F5A524]/5 border border-[#F5A524]/20 rounded-lg p-4 mb-3 flex items-start gap-2">
        <WarningCircle weight="fill" className="w-4 h-4 text-[#F5A524] mt-0.5 shrink-0" />
        <p className="text-[#D4D4D8] text-sm leading-relaxed">{tip}</p>
      </div>

      {plan.stretch.length === 0 && (
        <div
          className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mb-5 flex items-start gap-2"
          data-testid="budget-complete-banner"
        >
          <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <p className="text-emerald-100 text-sm leading-relaxed font-medium">
            {c.budget.complete_label}
          </p>
        </div>
      )}

      {/* Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Affordable */}
        <div className="bg-[#0A0A0A] border border-emerald-500/20 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle weight="fill" className="w-4 h-4 text-emerald-400" />
              <h3 className="text-white text-sm font-medium">{c.budget.affordable}</h3>
            </div>
            <span className="text-emerald-400 text-xs font-['JetBrains_Mono']" data-testid="budget-affordable-count">
              {c.budget.items_count(plan.affordable.length)}
            </span>
          </div>
          <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-2">
            {plan.affordable.length === 0 && (
              <li className="text-[#71717A] text-xs italic">—</li>
            )}
            {plan.affordable.map((item, i) => renderItem(item, i))}
          </ul>
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
            <span className="text-[#71717A]">{c.budget.total_used}</span>
            <span className="text-white font-['JetBrains_Mono']" data-testid="budget-total-used">
              ${plan.totalUsed.toLocaleString()} <span className="text-[#71717A]">/ ${budget.toLocaleString()}</span>
            </span>
          </div>
          <div className="flex items-center justify-between text-xs mt-1">
            <span className="text-[#71717A]">{c.budget.remaining}</span>
            <span className="text-emerald-400 font-['JetBrains_Mono']">
              ${plan.remaining.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Stretch */}
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <ShoppingBag weight="regular" className="w-4 h-4 text-[#A1A1AA]" />
              <h3 className="text-white text-sm font-medium">{c.budget.stretch}</h3>
            </div>
            <span className="text-[#71717A] text-xs font-['JetBrains_Mono']">
              {c.budget.items_left(plan.stretch.length)}
            </span>
          </div>
          <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-2">
            {plan.stretch.length === 0 && (
              <li className="text-emerald-400 text-xs italic">✓</li>
            )}
            {plan.stretch.map((item, i) =>
              renderItem(item, plan.affordable.length + i, true)
            )}
          </ul>
        </div>
      </div>

      {/* Owned list (collapsed footer section) */}
      {plan.owned.length > 0 && (
        <div className="mt-4 bg-[#0A0A0A] border border-white/5 rounded-lg p-4" data-testid="owned-gear-list">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle weight="fill" className="w-4 h-4 text-[#52525B]" />
              <h3 className="text-[#A1A1AA] text-sm font-medium">{c.budget.owned_title}</h3>
            </div>
            <span className="text-[#71717A] text-xs font-['JetBrains_Mono']">
              {c.budget.items_count(plan.owned.length)}
            </span>
          </div>
          <ul className="space-y-1.5 grid grid-cols-1 md:grid-cols-2 gap-x-4">
            {plan.owned.map((item, i) => (
              <li key={item.id} className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked
                  onChange={() => onToggleOwn(item.id)}
                  className="w-3.5 h-3.5 rounded border-white/20 bg-[#121214] accent-[#F5A524] cursor-pointer shrink-0"
                  data-testid={`gear-checkbox-${item.id}`}
                />
                <span className="flex-1 truncate text-[#71717A] line-through">{item.name}</span>
                <span className="text-[#52525B] font-['JetBrains_Mono'] text-[10px]">${item.cost}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Separate print-only snapshot rendered inside the page's print block.
export function BudgetPrintBlock({ budget, tierLabel, plan, c }) {
  return (
    <div className="mb-6 break-inside-avoid">
      <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">
        {c.budget.title}
      </h2>
      <p className="text-sm text-gray-700 mb-2">
        <strong>{c.budget.label}:</strong> ${budget.toLocaleString()} — <em>{tierLabel}</em>
        {plan.owned.length > 0 && (
          <span className="text-gray-600">
            &nbsp;· {c.budget.owned_label}: {plan.owned.length} (${plan.ownedValue.toLocaleString()} {c.budget.saved})
          </span>
        )}
      </p>
      <p className="text-xs text-gray-600 italic mb-3">{c.budget[`tip_${plan.tier}`]}</p>
      <div className="grid grid-cols-2 gap-6">
        <div>
          <h3 className="text-sm font-semibold mb-2">{c.budget.affordable} ({plan.affordable.length})</h3>
          <ol className="list-decimal ml-5 space-y-1">
            {plan.affordable.map((item) => (
              <li key={item.id} className="text-xs text-gray-700">
                {item.name} <span className="text-gray-500">${item.cost}</span>
              </li>
            ))}
          </ol>
          <p className="text-xs mt-2 text-gray-600">
            {c.budget.total_used}: <strong>${plan.totalUsed.toLocaleString()}</strong> / ${budget.toLocaleString()}
            &nbsp;· {c.budget.remaining}: <strong>${plan.remaining.toLocaleString()}</strong>
          </p>
        </div>
        <div>
          <h3 className="text-sm font-semibold mb-2">{c.budget.stretch} ({plan.stretch.length})</h3>
          <ol className="list-decimal ml-5 space-y-1" start={plan.affordable.length + 1}>
            {plan.stretch.map((item) => (
              <li key={item.id} className="text-xs text-gray-600">
                {item.name} <span className="text-gray-500">${item.cost}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
      {plan.owned.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-semibold mb-2">
            {c.budget.owned_title} ({plan.owned.length})
          </h3>
          <ul className="list-disc ml-5 grid grid-cols-2 gap-x-6">
            {plan.owned.map((item) => (
              <li key={item.id} className="text-xs text-gray-600 line-through">
                {item.name}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
